import { fn } from '@ember/helper';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import {
  PrismCheckbox,
  PrismComboSelectInput,
  PrismInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import type MarketParityParityManagerController from 'lumen/controllers/market/parity/parity-manager';
import type MarketParityParityManagerAddissueController from 'lumen/controllers/market/parity/parity-manager/addissue';
import type { NewIssue } from 'lumen/controllers/market/parity/parity-manager/addissue';
import dayjsFormat from 'lumen/helpers/dayjs-format';
import type { SelectInputOptionWithId } from 'lumen/utils/constants/pi-constant-values';

export const CHANNEL_PLATFORM = 'channel_platform';

type ChannelOption = SelectInputOptionWithId<string | number>;

type PlatformOption =
  MarketParityParityManagerAddissueController['platformOptions'][number];

interface ParityParityManagerChannelRateFormSignature {
  Args: {
    back: () => void;
    currencyCode: string | undefined;
    issue: NewIssue;
    next: () => void;
    parentController: MarketParityParityManagerController;
    platformOptions: PlatformOption[];
    setIssueProperty: (property: string, newValue: unknown) => void;
    setIssuePropertyFromEvent: (property: string, event: Event) => void;
    toggleValue: (field: string, event: Event) => void;
  };
}

export default class ParityParityManagerChannelRateForm extends Component<ParityParityManagerChannelRateFormSignature> {
  get isChannelRateValid(): boolean {
    let isValidChannelRateByIssueView = Number(this.issueChannelRate) > 0;
    // unrestrict channel rate for b&s
    if (!this.args.parentController.isBaitAndSwitchView) {
      isValidChannelRateByIssueView &&=
        parseFloat(this.issueChannelRate ?? '') <
        parseFloat(this.issueBaseRate ?? '');
    }
    return Boolean(
      this.issueBaseRate &&
      this.issueChannelRate &&
      isValidChannelRateByIssueView,
    );
  }

  get isFormValid(): boolean {
    return Boolean(this.issueChannel && this.isChannelRateValid);
  }

  get issueBaseRate(): string | undefined {
    return this.args.issue?.base_rate;
  }

  get issueChannel(): NewIssue['channel'] {
    return this.args.issue?.channel;
  }

  get issueChannelRate(): string | undefined {
    return this.args.issue?.channel_rate;
  }

  get possibleChannelsByIssueTypeValue(): ChannelOption | undefined {
    return this.args.parentController.possibleChannels.find(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (option) => option.entity.id === this.args.issue.channel?.id,
    );
  }

  get selectedPlatform(): PlatformOption | undefined {
    return this.args.platformOptions.find(
      (option) => option.id === this.args.issue[CHANNEL_PLATFORM],
    );
  }

  @action
  handlePossibleChannelsByIssueTypeChange(option: ChannelOption): void {
    this.args.setIssueProperty('channel', option.entity);
  }

  @action
  setSelectedPlatformId(option: PlatformOption): void {
    this.args.setIssueProperty(CHANNEL_PLATFORM, option.id);
  }

  <template>
    <div class="flex flex-col p-500" id="add-issue-form">
      <h2 class="header-title pb-400">
        {{t "parity_manager.add_issue.channel_rate_info"}}
      </h2>
      <div class="flex w-full flex-col pb-400">
        <label class="input-label required-indicator">
          {{t "parity.channel"}}
        </label>
        <PrismComboSelectInput
          @isSearchable={{true}}
          @isSecondary={{true}}
          @onChange={{this.handlePossibleChannelsByIssueTypeChange}}
          @options={{@parentController.possibleChannels}}
          @placeholder={{t "parity.channel"}}
          @value={{this.possibleChannelsByIssueTypeValue}}
        />
      </div>

      <div class="grid grid-cols-2 gap-400">
        <PrismInput
          @errorMessage={{t "parity_manager.validation.channel_rate"}}
          @hasErrors={{not this.isChannelRateValid}}
          @inlineLabel={{@currencyCode}}
          @label={{t "parity.channel_rate"}}
          @onInput={{fn @setIssuePropertyFromEvent "channel_rate"}}
          @required={{true}}
          @requiredErrorMessage={{t "general.required"}}
          @value={{@issue.channel_rate}}
        />
        <div>
          <label class="input-label">{{t "rates.type"}}</label>
          <div class="checkbox flex items-center">
            <PrismCheckbox
              @checked={{@issue.channel_cancellation}}
              @onChange={{fn @toggleValue "channel_cancellation"}}
            >
              {{t "general.cancellable"}}
            </PrismCheckbox>
          </div>
        </div>
      </div>

      <div class="flex w-full flex-col">
        <PrismInput
          @label={{t "parity_manager.channel_room_name"}}
          @onInput={{fn @setIssuePropertyFromEvent "channel_room_name"}}
          @value={{@issue.channel_room_name}}
        />
      </div>

      <div class="grid grid-cols-2 gap-400">
        <div class="flex flex-col">
          <label class="input-label required-indicator">
            {{t "general.arrival_date"}}
          </label>
          <PrismInput
            @disabled={{true}}
            @type="text"
            {{! @glint-expect-error: Argument of type 'string | Dayjs | null' is not assignable to parameter of type 'Dayjs | undefined'. }}
            @value={{dayjsFormat @issue.arrival_date format="L"}}
          />
        </div>
        <div class="flex flex-col">
          <label class="input-label required-indicator">
            {{t "rates.los"}}
          </label>
          <PrismInput @disabled={{true}} @type="text" @value={{@issue.los}} />
        </div>
      </div>

      {{#if @parentController.isMajorOtaView}}
        <div class="grid grid-cols-2 gap-400">
          <div class="flex flex-col">
            <label class="input-label required-indicator">
              {{t "general.platform.plural" count=1}}
            </label>
            <PrismComboSelectInput
              @isSecondary={{true}}
              @onChange={{this.setSelectedPlatformId}}
              @options={{@platformOptions}}
              @value={{this.selectedPlatform}}
            />
          </div>
        </div>
      {{/if}}
    </div>

    <div class="settings-modal__footer flex justify-end">
      <GeneralFormFieldsCancelAndConfirm
        @cancel={{@back}}
        @cancelText={{t "general.back"}}
        @confirm={{@next}}
        @confirmText={{t "general.next_step"}}
        @isConfirmDisabled={{not this.isFormValid}}
      />
    </div>
  </template>
}
